import { useEffect, useMemo } from "react";
import { createSelectSchema, FieldSchema, FieldType, Schema } from "../../../../hooks/useSchema";
import { useCMSSettingsContext } from "../../../CMS/Configuration/CMSSettingsContext";

export interface PreconfiguredField {
  code: string;
  label: string;
  type: string;
  options1?: string;
  options2?: string;
}

const doubleSplit = (text?: string) => text?.split("\n").map(x => x.trim()).filter(x => !!x).map(x => x.split(";").map(x => x.trim())) || [];


type ReadPF = (args: string[]) => PreconfiguredField;
const readPreconfiguredField: ReadPF = ([code, label, type, options1, options2]) => ({
  code: code.replace(/^-\s*/, ""),
  label,
  type,
  options1,
  options2,
});

const EmptyX: Record<string, { fields: string[] }> = {};

export const usePreconfiguredFields = (
  blockType: string | undefined, grandType: "custom_fields_block" | "configurable_table_block",
  fieldsConfiguration: string | null | undefined) => {
  const cmsSettings = useCMSSettingsContext();

  useEffect(() => {
    cmsSettings.ensureLoaded();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const blocksSettings = grandType === "configurable_table_block"
    ? cmsSettings.settings.configurable_table_block_settings
    : grandType === "custom_fields_block"
        ? cmsSettings.settings.custom_fields_block_settings
        : EmptyX;

  const x = useMemo(() => {
    const configsText = fieldsConfiguration || "";
    const fields: PreconfiguredField[] = doubleSplit(configsText)
      .map(readPreconfiguredField);

    const blockSettings = blockType && blocksSettings
      ? (blocksSettings[blockType]?.fields || [])
      : [];


    const fieldsFromSettings: PreconfiguredField[] = blockSettings
      .map(f => f.split(";").map(s => s.trim()))
      .map(readPreconfiguredField);

    const fieldsAll = [
      ...fieldsFromSettings.filter(f => !fields.find(fx => fx.code === f.code)),
      ...fields,
    ];

    const schema = fieldsAll.reduce<Schema>((r,f) => {
      let fs: FieldSchema = { type: f.type as any, label: f.label };
      if(f.type === FieldType.select || f.type === FieldType.multiselect) {
        fs = createSelectSchema((f.options1 || "").split(",").map(s => s.trim()).map(s => ({ label: s, value: s })), { ...fs });
      }
      r[f.code] = fs;
      return r;
    }, {});

    const listFields = blockType && blocksSettings
      ? (blocksSettings[blockType] as any)?.list_fields as string || ""
      : "";

    return {
      fields: fieldsAll,
      schema,
      fieldsCodes: new Set(fieldsAll.map(f => f.code)),
      listFields,
    }
    
  }, [fieldsConfiguration, blocksSettings, blockType]);

  return x;
}
